'use client';

import { useEffect, useState, useRef } from 'react';
import { SignInButton, SignedIn, SignedOut, UserButton } from '@clerk/nextjs';
import Link from 'next/link';
import { LandingShell, Navbar, Footer } from '@vanmoose/landing-core';
import {
  trackSEOPageView,
  trackSEOCTAClick,
  setSEOReferrer,
  type SEOIntentType,
} from '../../../lib/analytics/seo';
import { RepetixLogo } from '../RepetixLogo';
import { DiagnosticQuestion, type SubscriptionEstimate } from './DiagnosticQuestion';

export interface SEOPageV2Config {
  pageSlug: string;
  headline: string;
  subheadline: string;
  ctaText: string;
  story: string;
  costs: string[];
  relief: string;
  proofLine?: string;
  faqs: { question: string; answer: string }[];
  internalLinks?: { anchor: string; href: string }[];
  relatedPages?: { href: string; title: string }[];
}

const navLinks = [
  { label: 'How It Works', href: '/#how-it-works' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'FAQ', href: '/#faq' },
];

const footerColumns = [
  {
    title: 'Product',
    links: [
      { label: 'How it works', href: '/#how-it-works' },
      { label: 'Pricing', href: '/#pricing' },
      { label: 'FAQ', href: '/#faq' },
      { label: 'Docs', href: '/docs' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Subscription tracker', href: '/subscription-tracker' },
      { label: 'Free trial tracker', href: '/free-trial-tracker' },
      { label: 'Forgotten subscriptions', href: '/forgotten-subscriptions' },
      { label: 'Cancel subscriptions', href: '/cancel-subscriptions' },
    ],
  },
  {
    title: 'For Teams',
    links: [{ label: 'Tracktron →', href: 'https://tracktron.cc/business' }],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
      { label: 'Refund', href: '/refund' },
      { label: 'Help', href: '/help' },
    ],
  },
];

function AuthCTA() {
  return (
    <div className="flex items-center gap-3">
      <SignedOut>
        <SignInButton mode="modal">
          <button
            type="button"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors duration-150"
          >
            Sign In
          </button>
        </SignInButton>
      </SignedOut>
      <SignedIn>
        <Link
          href="/dashboard"
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors duration-150 mr-2"
        >
          Dashboard
        </Link>
        <UserButton afterSignOutUrl="/" />
      </SignedIn>
    </div>
  );
}

export function SEOLandingPageV2({
  pageSlug,
  headline,
  subheadline,
  ctaText,
  story,
  costs,
  relief,
  proofLine,
  faqs,
  internalLinks,
  relatedPages,
}: SEOPageV2Config) {
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const signInButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    trackSEOPageView({
      page_slug: pageSlug,
      intent_type: pageSlug as SEOIntentType,
    });
    if (typeof window !== 'undefined' && (window as { posthog?: { capture: (e: string, p: object) => void } }).posthog) {
      (window as { posthog?: { capture: (e: string, p: object) => void } }).posthog!.capture('diagnostic_shown', { page_slug: pageSlug });
    }
  }, [pageSlug]);

  const handleCTAClick = (location: 'hero' | 'bottom') => {
    trackSEOCTAClick({
      page_slug: pageSlug,
      cta_variant: ctaText,
      cta_location: location,
    });
    setSEOReferrer(pageSlug);
    setShowDiagnostic(true);
  };

  const handleDiagnosticAnswer = (_estimate: SubscriptionEstimate) => {
    setTimeout(() => {
      signInButtonRef.current?.click();
    }, 100);
  };

  return (
    <LandingShell theme="repetix">
      <Navbar
        logo={
          <div className="flex items-center gap-2.5">
            <RepetixLogo size="lg" />
            <span className="font-semibold text-[var(--landing-text-primary)]">
              Repetix
            </span>
          </div>
        }
        links={navLinks}
        ctaElement={<AuthCTA />}
      />

      {/* Diagnostic Modal */}
      {showDiagnostic && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm">
          <div className="relative max-w-lg w-full mx-4 bg-[var(--landing-bg-card)] rounded-2xl p-8 border border-[var(--landing-border)]">
            <button
              type="button"
              onClick={() => setShowDiagnostic(false)}
              className="absolute top-4 right-4 text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)] transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <DiagnosticQuestion
              pageSlug={pageSlug}
              onAnswer={handleDiagnosticAnswer}
            />
          </div>
        </div>
      )}

      {/* Hidden SignInButton — fired after diagnostic */}
      <SignedOut>
        <div className="hidden">
          <SignInButton mode="redirect" forceRedirectUrl="/dashboard">
            <button ref={signInButtonRef} type="button">Sign in</button>
          </SignInButton>
        </div>
      </SignedOut>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="headline-xl mb-6">{headline}</h1>
          <p className="body-lg max-w-readable mb-10 mx-auto">{subheadline}</p>
          <SignedOut>
            <button
              type="button"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-xl bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-all duration-200 shadow-lg shadow-[var(--accent-glow)]"
              onClick={() => handleCTAClick('hero')}
            >
              {ctaText}
            </button>
          </SignedOut>
          <SignedIn>
            <Link
              href="/dashboard"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-xl bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-all duration-200 shadow-lg shadow-[var(--accent-glow)]"
            >
              Go to dashboard
            </Link>
          </SignedIn>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <p className="text-lg text-[var(--landing-text-secondary)] leading-relaxed whitespace-pre-line">
            {story}
          </p>
        </div>
      </section>

      {/* Costs */}
      <section className="py-16 px-6 border-t border-[var(--landing-border)]">
        <div className="max-w-2xl mx-auto">
          <ul className="space-y-4">
            {costs.map((cost, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-[var(--accent)] mt-1 font-semibold">$</span>
                <span className="text-[var(--landing-text-secondary)]">{cost}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Relief */}
      <section className="py-16 px-6 border-t border-[var(--landing-border)]">
        <div className="max-w-2xl mx-auto">
          <p className="text-lg text-[var(--landing-text-secondary)] leading-relaxed">
            {relief}
          </p>
        </div>
      </section>

      {/* Internal Links */}
      {internalLinks && internalLinks.length > 0 && (
        <section className="py-8 px-6">
          <div className="max-w-2xl mx-auto">
            <div className="space-y-3">
              {internalLinks.slice(0, 2).map((link, i) => (
                <p key={i} className="text-[var(--landing-text-muted)] text-sm">
                  <Link
                    href={link.href}
                    className="text-[var(--landing-text-secondary)] hover:text-[var(--landing-text-primary)] underline underline-offset-2 transition-colors"
                  >
                    {link.anchor}
                  </Link>
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mid CTA */}
      <section className="py-20 px-6 bg-[var(--landing-bg-muted)]">
        <div className="max-w-xl mx-auto text-center">
          {proofLine && (
            <p className="text-[var(--landing-text-muted)] text-sm mb-6">{proofLine}</p>
          )}
          <SignedOut>
            <button
              type="button"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-xl bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-all duration-200 shadow-lg shadow-[var(--accent-glow)]"
              onClick={() => handleCTAClick('bottom')}
            >
              {ctaText}
            </button>
          </SignedOut>
          <SignedIn>
            <Link
              href="/dashboard"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold rounded-xl bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-all duration-200 shadow-lg shadow-[var(--accent-glow)]"
            >
              Go to dashboard
            </Link>
          </SignedIn>
          <p className="text-[var(--landing-text-muted)] text-sm mt-4">Free. No credit card.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 border-t border-[var(--landing-border)]">
        <div className="max-w-2xl mx-auto">
          <div className="space-y-8">
            {faqs.map((faq, i) => (
              <div key={i}>
                <h2 className="text-[var(--landing-text-primary)] font-medium mb-2">{faq.question}</h2>
                <p className="text-[var(--landing-text-secondary)] text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Pages */}
      {relatedPages && relatedPages.length > 0 && (
        <section className="py-12 px-6 border-t border-[var(--landing-border)]">
          <div className="max-w-2xl mx-auto">
            <div className="flex flex-wrap gap-4 justify-center">
              {relatedPages.map((page, i) => (
                <Link
                  key={i}
                  href={page.href}
                  className="text-sm text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)] transition-colors"
                >
                  {page.title}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer
        logo={
          <div className="flex items-center gap-2.5">
            <RepetixLogo size="lg" />
            <span className="font-semibold text-[var(--landing-text-primary)]">
              Repetix
            </span>
          </div>
        }
        columns={footerColumns}
        bottomLinks={[
          { label: 'Privacy', href: '/privacy' },
          { label: 'Terms', href: '/terms' },
        ]}
      />
    </LandingShell>
  );
}
